import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ShoppingBag as ShoppingBagIcon } from "../icons";
import { Search as SearchIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { T } from "./Translate";
import PublicPageLayout, { useAuthUser } from "../components/PublicPageLayout";
import { brandColors } from "../theme";

// "Precios que seguimos" (/precios): los productos más vendidos de tiendas
// argentinas cuyo precio guarda cada semana `trackProductPrices`
// (functions/priceHistory.js). La página baja una sola vez el índice
// (/precios.json, lo sirve la función `priceIndex`, cacheado en el CDN) y
// busca, filtra y ordena en el navegador: ninguna lectura de Firestore por
// visita. Los filtros viven en la URL (?q=&cat=&desc=&max=&cuotas=&orden=)
// para poder compartir una búsqueda. Cada tarjeta lleva a la tienda.
const PAGE_SIZE = 24;

// En notiline.com va por el rewrite de Hosting y la app de Android (que corre
// en https://localhost) lo pide al dominio. En `npm start` sale de
// public/precios.json, que se genera con `node scripts/buildPriceIndex.js`
// (no se sube ni se despliega: taparía el rewrite).
const INDEX_URL = process.env.NODE_ENV === "development" || window.location.hostname.endsWith("notiline.com")
  ? "/precios.json"
  : "https://notiline.com/precios.json";

const DISCOUNTS = [10, 20, 30, 50];
const MAX_PRICES = [20000, 50000, 100000, 300000, 1000000];
const SORTS = [
  { id: "", name: "Mayor descuento" },
  { id: "menor", name: "Menor precio" },
  { id: "mayor", name: "Mayor precio" },
];

const money = (value) => `$ ${Math.round(Number(value) || 0).toLocaleString("es-AR")}`;

// Minúsculas y sin acentos, para que "camara" encuentre "Cámara".
const normalize = (text) =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const ProductCard = ({ item }) => (
  <Card sx={{ height: "100%", borderRadius: 3, backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}` }}>
    <CardActionArea
      component="a"
      href={item.u}
      target="_blank"
      rel="noopener noreferrer nofollow"
      sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
    >
      {item.i ? (
        <Box
          component="img"
          src={item.i}
          alt={item.n || ""}
          loading="lazy"
          sx={{ width: "100%", height: 180, objectFit: "contain", display: "block", bgcolor: "#fff" }}
        />
      ) : (
        <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: brandColors.backgroundDefault }}>
          <ShoppingBagIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {item.s}
        </Typography>
        <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere", lineHeight: 1.3 }}>{item.n}</Typography>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{money(item.p)}</Typography>
          {item.d > 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary", textDecoration: "line-through" }}>
              {money(item.l)}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
          {item.d > 0 && <Chip size="small" color="success" label={`${item.d}% OFF`} />}
          {item.q > 1 && <Chip size="small" variant="outlined" label={`${item.q} cuotas sin interés`} />}
          {(item.pr || []).slice(0, 2).map((p) => (
            <Chip key={p} size="small" variant="outlined" label={p} />
          ))}
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
);

const FilterSelect = ({ label, value, onChange, children }) => (
  <FormControl size="small" sx={{ minWidth: 150, flex: { xs: "1 1 45%", sm: "0 0 auto" } }}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {children}
    </Select>
  </FormControl>
);

const PriceTracker = () => {
  const { checked, user } = useAuthUser();
  const [index, setIndex] = useState(null);
  const [failed, setFailed] = useState(false);
  const [shown, setShown] = useState(PAGE_SIZE);
  const [params, setParams] = useSearchParams();

  const q = params.get("q") || "";
  const cat = params.get("cat") || "";
  const minDiscount = Number(params.get("desc")) || 0;
  const maxPrice = Number(params.get("max")) || 0;
  const onlyInstallments = params.get("cuotas") === "1";
  const sort = params.get("orden") || "";
  const hasFilters = Boolean(q || cat || minDiscount || maxPrice || onlyInstallments || sort);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let active = true;
    fetch(INDEX_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => active && setIndex(data))
      .catch((e) => {
        console.error("Error cargando el índice de precios:", e);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // Texto de búsqueda de cada producto (nombre, marca, tienda y categoría),
  // armado una sola vez.
  const searchable = useMemo(() => {
    if (!index) return [];
    const catNames = Object.fromEntries(index.categories.map((c) => [c.id, c.name]));
    return index.items.map((item) => ({
      item,
      text: normalize(`${item.n} ${item.b} ${item.s} ${catNames[item.c] || ""}`),
    }));
  }, [index]);

  // Todo menos la categoría: así cada chip muestra cuántos hay con la
  // búsqueda y los demás filtros actuales.
  const matching = useMemo(() => {
    const words = normalize(q).split(/\s+/).filter(Boolean);
    return searchable
      .filter(({ item, text }) =>
        words.every((w) => text.includes(w)) &&
        item.d >= minDiscount &&
        (!maxPrice || item.p <= maxPrice) &&
        (!onlyInstallments || item.q > 1))
      .map(({ item }) => item);
  }, [searchable, q, minDiscount, maxPrice, onlyInstallments]);

  const counts = useMemo(() => {
    const c = {};
    matching.forEach((item) => {
      c[item.c] = (c[item.c] || 0) + 1;
    });
    return c;
  }, [matching]);

  const results = useMemo(() => {
    const list = cat ? matching.filter((item) => item.c === cat) : [...matching];
    if (sort === "menor") list.sort((a, b) => a.p - b.p);
    else if (sort === "mayor") list.sort((a, b) => b.p - a.p);
    return list; // el índice ya viene por mayor descuento
  }, [matching, cat, sort]);

  // Al cambiar la búsqueda o un filtro se vuelve a la primera página.
  useEffect(() => {
    setShown(PAGE_SIZE);
  }, [q, cat, minDiscount, maxPrice, onlyInstallments, sort]);

  return (
    <PublicPageLayout title={<T>Precios que seguimos</T>} loading={!checked} isGuest={!user} showAd>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            <T>Precios que seguimos</T>
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 1 }}>
            <T>Cada semana guardamos el precio de los productos más vendidos de tiendas argentinas.</T>
            {index?.updated && (
              <>
                {" "}
                <T>Última actualización:</T> {index.updated.split("-").reverse().join("/")}.
              </>
            )}
          </Typography>
        </Box>

        <TextField
          value={q}
          onChange={(e) => setParam("q", e.target.value)}
          placeholder="Buscar: heladera, zapatillas, Samsung, protector solar…"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "Buscar productos", enterKeyHint: "search" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: q && (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => setParam("q", "")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {index && (
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            <Chip
              label={<><T>Todas</T> ({matching.length})</>}
              color={cat ? "default" : "primary"}
              onClick={() => setParam("cat", "")}
            />
            {index.categories
              .filter((c) => counts[c.id] || c.id === cat)
              .map((c) => (
                <Chip
                  key={c.id}
                  label={<><T>{c.name}</T> ({counts[c.id] || 0})</>}
                  color={cat === c.id ? "primary" : "default"}
                  onClick={() => setParam("cat", cat === c.id ? "" : c.id)}
                />
              ))}
          </Stack>
        )}

        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <FilterSelect label={<T>Descuento</T>} value={minDiscount || ""} onChange={(v) => setParam("desc", v)}>
            <MenuItem value="">
              <T>Cualquiera</T>
            </MenuItem>
            {DISCOUNTS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}% <T>o más</T>
              </MenuItem>
            ))}
          </FilterSelect>
          <FilterSelect label={<T>Precio</T>} value={maxPrice || ""} onChange={(v) => setParam("max", v)}>
            <MenuItem value="">
              <T>Cualquiera</T>
            </MenuItem>
            {MAX_PRICES.map((p) => (
              <MenuItem key={p} value={p}>
                <T>Hasta</T> {money(p)}
              </MenuItem>
            ))}
          </FilterSelect>
          <FilterSelect label={<T>Ordenar</T>} value={sort} onChange={(v) => setParam("orden", v)}>
            {SORTS.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                <T>{s.name}</T>
              </MenuItem>
            ))}
          </FilterSelect>
          <Chip
            label={<T>Cuotas sin interés</T>}
            color={onlyInstallments ? "primary" : "default"}
            variant={onlyInstallments ? "filled" : "outlined"}
            onClick={() => setParam("cuotas", onlyInstallments ? "" : "1")}
          />
          {hasFilters && (
            <Button size="small" onClick={() => setParams(new URLSearchParams(), { replace: true })} sx={{ textTransform: "none" }}>
              <T>Limpiar filtros</T>
            </Button>
          )}
        </Stack>

        {failed ? (
          <Typography sx={{ color: "text.secondary" }}>
            <T>No pudimos cargar los precios. Probá de nuevo en un rato.</T>
          </Typography>
        ) : !index ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : results.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            <T>No encontramos productos con esa búsqueda.</T>
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {results.length.toLocaleString("es-AR")} <T>productos</T>
            </Typography>
            {/* En un Box: el Stack les pone margin 0 a sus hijos y le borraría
                al Grid el margen negativo del spacing (queda corrido a la derecha). */}
            <Box>
              <Grid container spacing={2}>
                {results.slice(0, shown).map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <ProductCard item={item} />
                  </Grid>
                ))}
              </Grid>
            </Box>
            {shown < results.length && (
              <Stack alignItems="center">
                <Button variant="outlined" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                  <T>Ver más</T>
                </Button>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </PublicPageLayout>
  );
};

export default PriceTracker;
